import { useState, useCallback, useRef } from "react";
import { translateChatError } from "@/lib/chatErrors";
import type { ChatMessage } from "@/components/chat/MessageList";

export interface PendingImportTransaction {
  transaction_date: string;
  transaction_type: string;
  ticker: string;
  market?: string;
  units: number;
  price_per_unit: number;
  currency?: string;
  fees?: number;
}

export interface PendingImport {
  token: string;
  portfolioName: string;
  currency: string;
  transactionCount: number;
  transactionsPreview: PendingImportTransaction[];
}

interface UseChatStreamOptions {
  portfolioSlug?: string;
  context?: string;
  preservePendingImport?: boolean;
  onConversationCreated?: (conversationId: string) => void;
  onStreamComplete?: () => void;
}

export function useChatStream({ portfolioSlug, context, preservePendingImport, onConversationCreated, onStreamComplete }: UseChatStreamOptions = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isStreamingRef = useRef(false);

  const sendMessage = useCallback(
    async (content: string, file?: File) => {
      if (isStreamingRef.current) return;

      // Add user message optimistically
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content,
        createdAt: new Date().toISOString(),
        file: file ? { name: file.name } : undefined,
      };

      // Add placeholder for assistant message
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);
      isStreamingRef.current = true;
      if (!preservePendingImport) {
        setPendingImport(null);
      }

      abortControllerRef.current = new AbortController();

      try {
        // Build form data for unified endpoint
        const formData = new FormData();
        formData.append("content", content);

        if (portfolioSlug) {
          formData.append("portfolio_slug", portfolioSlug);
        }
        if (context) {
          formData.append("context", context);
        }
        if (file) {
          formData.append("file", file);
        }
        if (conversationId) {
          formData.append("conversation_id", conversationId);
        }

        const response = await fetch("/api/chat/stream", {
          method: "POST",
          body: formData,
          credentials: "include",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error("Failed to connect to chat stream");
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = done ? "" : (lines.pop() || ""); // Flush buffer on done

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              // Event type line (content, done, error) - skip, we parse data below
              continue;
            }

            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));

                // Handle start event - connection established
                if (data.conversation_id && !conversationId) {
                  setConversationId(data.conversation_id);
                  onConversationCreated?.(data.conversation_id);
                }

                // Handle content chunks
                if (data.content) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg.role === "assistant") {
                      updated[updated.length - 1] = {
                        ...lastMsg,
                        content: (lastMsg.content || "") + data.content,
                      };
                    }
                    return updated;
                  });
                }

                // Handle pending import from done event
                if (data.pending_import) {
                  setPendingImport({
                    token: data.pending_import.token,
                    portfolioName: data.pending_import.portfolio_name,
                    currency: data.pending_import.currency,
                    transactionCount: data.pending_import.transaction_count,
                    transactionsPreview: data.pending_import.transactions_preview || [],
                  });
                }
                
                if (data.delegate_used === true) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg?.role === "assistant") {
                      updated[updated.length - 1] = { ...lastMsg, delegateUsed: true };
                    }
                    return updated;
                  });
                }
                
                if (data.code || data.message) {
                  const text = data.code ? translateChatError(data.code) : (data.message as string);
                  console.error("Chat error:", data.code || data.message);
                  setMessages((prev) => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg?.role === "assistant") {
                      updated[updated.length - 1] = { ...lastMsg, content: text };
                    }
                    return updated;
                  });
                }
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
            }
          }

          if (done) break;
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          // User stopped streaming — remove the last assistant message (partial or empty)
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            return last?.role === "assistant" ? prev.slice(0, -1) : prev;
          });
        } else {
          console.error("Stream error:", error);
          setMessages((prev) => prev.filter((m) => m.role !== "assistant" || m.content));
        }
      } finally {
        setIsStreaming(false);
        isStreamingRef.current = false;
        abortControllerRef.current = null;
        onStreamComplete?.();
      }
    },
    [portfolioSlug, context, preservePendingImport, conversationId, onConversationCreated, onStreamComplete]
  );

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const loadConversation = useCallback((id: string, loadedMessages: ChatMessage[]) => {
    setConversationId(id);
    setMessages(loadedMessages);
  }, []);

  const startNewChat = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setPendingImport(null);
  }, []);

  const clearPendingImport = useCallback(() => {
    setPendingImport(null);
  }, []);

  return {
    messages,
    isStreaming,
    conversationId,
    pendingImport,
    sendMessage,
    stopStreaming,
    loadConversation,
    startNewChat,
    clearPendingImport,
  };
}
